import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users,
  FileText,
  Shield,
  Search,
  RefreshCw,
  Loader2,
  ArrowLeft,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  Download,
  Filter,
  ChevronDown,
  Calendar,
  Building2,
  Phone,
  Mail,
  MapPin,
  AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminSellerIntakePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [consentRecords, setConsentRecords] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [activeTab, setActiveTab] = useState('leads');
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [sellerFilter, setSellerFilter] = useState('all');
  const [leadTypeFilter, setLeadTypeFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialogs
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedConsent, setSelectedConsent] = useState(null);
  const [showLeadDialog, setShowLeadDialog] = useState(false);
  const [showConsentDialog, setShowConsentDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('dme_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, leadsRes, consentRes, sellersRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/seller-intake/stats`, { headers }),
        axios.get(`${API_URL}/api/admin/seller-intake/leads`, { headers }),
        axios.get(`${API_URL}/api/admin/seller-intake/consent-records`, { headers }),
        axios.get(`${API_URL}/api/admin/sellers`, { headers })
      ]);

      setStats(statsRes.data);
      setLeads(leadsRes.data.leads || []);
      setConsentRecords(consentRes.data.records || []);
      setSellers(sellersRes.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApproveLead = async (leadId) => {
    try {
      const token = localStorage.getItem('dme_token');
      await axios.post(
        `${API_URL}/api/admin/seller-intake/leads/${leadId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Lead approved');
      fetchData();
      setShowLeadDialog(false);
    } catch (error) {
      toast.error('Failed to approve lead');
    }
  };

  const handleRejectLead = async () => {
    if (!selectedLead) return;
    try {
      const token = localStorage.getItem('dme_token');
      await axios.post(
        `${API_URL}/api/admin/seller-intake/leads/${selectedLead.id}/reject`,
        { reason: rejectReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Lead rejected');
      fetchData();
      setShowRejectDialog(false);
      setShowLeadDialog(false);
      setRejectReason('');
    } catch (error) {
      toast.error('Failed to reject lead');
    }
  };

  const viewConsentDetails = async (recordId) => {
    try {
      const token = localStorage.getItem('dme_token');
      const res = await axios.get(
        `${API_URL}/api/admin/seller-intake/consent-records/${recordId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedConsent(res.data);
      setShowConsentDialog(true);
    } catch (error) {
      toast.error('Failed to load consent details');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-blue-100 text-blue-800',
      sold: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{status}</Badge>;
  };

  const filteredLeads = leads.filter(lead => {
    if (statusFilter !== 'all' && lead.status !== statusFilter) return false;
    if (sellerFilter !== 'all' && lead.seller_id !== sellerFilter) return false;
    if (leadTypeFilter !== 'all' && lead.lead_type !== leadTypeFilter) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        lead.first_name?.toLowerCase().includes(term) ||
        lead.last_name?.toLowerCase().includes(term) ||
        lead.email?.toLowerCase().includes(term) ||
        lead.phone?.includes(term) ||
        lead.confirmation_id?.toLowerCase().includes(term)
      );
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-seller-intake-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/settings')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Seller Intake Hub</h1>
            <p className="text-gray-500 text-sm">Manage leads submitted by sellers with full consent tracking</p>
          </div>
        </div>
        <Button onClick={fetchData} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <FileText className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.total_leads || 0}</p>
                <p className="text-xs text-gray-500">Total Leads</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.pending_leads || 0}</p>
                <p className="text-xs text-gray-500">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.sold_leads || 0}</p>
                <p className="text-xs text-gray-500">Sold</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.rejected_leads || 0}</p>
                <p className="text-xs text-gray-500">Rejected</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Shield className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.total_consent_records || 0}</p>
                <p className="text-xs text-gray-500">Consent Records</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="leads">Leads</TabsTrigger>
          <TabsTrigger value="consent">TCPA Consent Audit</TabsTrigger>
        </TabsList>

        {/* Leads Tab */}
        <TabsContent value="leads" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex-1 min-w-[200px]">
              <Input
                placeholder="Search by name, email, phone, confirmation..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="sold">Sold</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sellerFilter} onValueChange={setSellerFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Seller" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sellers</SelectItem>
                {sellers.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.company_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={leadTypeFilter} onValueChange={setLeadTypeFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Lead Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="data_lead">Data Lead</SelectItem>
                <SelectItem value="cold_call">Cold Call</SelectItem>
                <SelectItem value="live_transfer">Live Transfer</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Leads Table */}
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Confirmation</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Seller</TableHead>
                  <TableHead>Source Domain</TableHead>
                  <TableHead>API Key</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Consent</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Assigned To</TableHead>
                  <TableHead>Sold To</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLeads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={13} className="text-center py-8 text-gray-500">
                      No leads found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLeads.map((lead) => (
                    <TableRow key={lead.id}>
                      <TableCell className="font-mono text-sm">{lead.confirmation_id}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{lead.first_name} {lead.last_name}</p>
                          <p className="text-xs text-gray-500">{lead.email}</p>
                          <p className="text-xs text-gray-500">{lead.phone}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {lead.city && `${lead.city}, `}{lead.state}
                        {lead.zip_code && <span className="text-gray-400 text-xs ml-1">{lead.zip_code}</span>}
                      </TableCell>
                      <TableCell className="text-sm">{lead.seller_name}</TableCell>
                      <TableCell className="text-xs">
                        <code>{lead.source_domain || '-'}</code>
                      </TableCell>
                      <TableCell className="text-xs">
                        <code>{lead.source_api_key_prefix ? `${lead.source_api_key_prefix}...` : '-'}</code>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{lead.lead_type}</Badge>
                      </TableCell>
                      <TableCell>
                        {lead.consent_tcpa ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : (
                          <AlertTriangle className="w-4 h-4 text-yellow-500" />
                        )}
                      </TableCell>
                      <TableCell>{getStatusBadge(lead.status)}</TableCell>
                      <TableCell className="text-sm">{lead.assigned_to_client_name || '-'}</TableCell>
                      <TableCell className="text-sm">
                        {lead.sold_to_client_name ? (
                          <div>
                            <p>{lead.sold_to_client_name}</p>
                            {lead.sold_at && <p className="text-xs text-gray-500">{new Date(lead.sold_at).toLocaleString()}</p>}
                          </div>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {new Date(lead.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => { setSelectedLead(lead); setShowLeadDialog(true); }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Consent Audit Tab */}
        <TabsContent value="consent" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-600" />
                TCPA Consent Records
              </CardTitle>
            </CardHeader>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Confirmation</TableHead>
                  <TableHead>Consumer</TableHead>
                  <TableHead>Seller</TableHead>
                  <TableHead>Consents</TableHead>
                  <TableHead>IP Address</TableHead>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {consentRecords.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                      No consent records found
                    </TableCell>
                  </TableRow>
                ) : (
                  consentRecords.map((record) => (
                    <TableRow key={record.id}>
                      <TableCell className="font-mono text-sm">{record.confirmation_id}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{record.consumer_name}</p>
                          <p className="text-xs text-gray-500">{record.consumer_email}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{record.seller_name}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {record.consent_contact && <Badge className="bg-green-100 text-green-800 text-xs">Contact</Badge>}
                          {record.consent_tcpa && <Badge className="bg-blue-100 text-blue-800 text-xs">TCPA</Badge>}
                          {record.consent_sms && <Badge className="bg-purple-100 text-purple-800 text-xs">SMS</Badge>}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{record.ip_address}</TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {new Date(record.consent_timestamp).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => viewConsentDetails(record.id)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Lead Detail Dialog */}
      <Dialog open={showLeadDialog} onOpenChange={setShowLeadDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Lead Details</DialogTitle>
            <DialogDescription>
              {selectedLead?.confirmation_id}
            </DialogDescription>
          </DialogHeader>
          {selectedLead && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500">Name</label>
                  <p className="font-medium">{selectedLead.first_name} {selectedLead.last_name}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Status</label>
                  <p>{getStatusBadge(selectedLead.status)}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Email</label>
                  <p className="flex items-center gap-2"><Mail className="w-4 h-4 text-gray-400" />{selectedLead.email}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Phone</label>
                  <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-gray-400" />{selectedLead.phone}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Location</label>
                  <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-gray-400" />{selectedLead.city}, {selectedLead.state} {selectedLead.zip_code}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Seller</label>
                  <p className="flex items-center gap-2"><Building2 className="w-4 h-4 text-gray-400" />{selectedLead.seller_name}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Lead Type</label>
                  <p><Badge variant="outline">{selectedLead.lead_type}</Badge></p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Created</label>
                  <p className="flex items-center gap-2"><Calendar className="w-4 h-4 text-gray-400" />{new Date(selectedLead.created_at).toLocaleString()}</p>
                </div>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg border">
                <h4 className="font-medium mb-3">Lead Journey (Admin Only)</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <label className="text-xs text-gray-500">Source Domain</label>
                    <p className="font-mono">{selectedLead.source_domain || '-'}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Source API Key</label>
                    <p className="font-mono">{selectedLead.source_api_key_prefix ? `${selectedLead.source_api_key_prefix}...` : '-'}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Assigned To</label>
                    <p>{selectedLead.assigned_to_client_name || '-'}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Last Assigned At</label>
                    <p>{selectedLead.last_assigned_at ? new Date(selectedLead.last_assigned_at).toLocaleString() : '-'}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Sold To</label>
                    <p>{selectedLead.sold_to_client_name || '-'}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Sold At</label>
                    <p>{selectedLead.sold_at ? new Date(selectedLead.sold_at).toLocaleString() : '-'}</p>
                  </div>
                </div>
                {Array.isArray(selectedLead.assignment_history) && selectedLead.assignment_history.length > 0 && (
                  <div className="mt-4">
                    <label className="text-xs text-gray-500">Assignment History</label>
                    <div className="mt-2 space-y-2">
                      {selectedLead.assignment_history.map((entry, idx) => (
                        <div key={`${entry.assignment_id || idx}`} className="text-xs bg-white rounded border p-2">
                          <p><strong>{entry.client_name || entry.client_id || 'Client'}</strong> • {entry.assigned_at ? new Date(entry.assigned_at).toLocaleString() : '-'}</p>
                          <p className="text-gray-500">Campaign: {entry.campaign_name || 'Manual'} • Bid: ${Number(entry.bid || 0).toFixed(2)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Consent Info */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Consent Status
                </h4>
                <div className="flex gap-3">
                  <span className={`text-sm ${selectedLead.consent_contact ? 'text-green-600' : 'text-red-600'}`}>
                    Contact: {selectedLead.consent_contact ? '✓' : '✗'}
                  </span>
                  <span className={`text-sm ${selectedLead.consent_tcpa ? 'text-green-600' : 'text-red-600'}`}>
                    TCPA: {selectedLead.consent_tcpa ? '✓' : '✗'}
                  </span>
                </div>
                {selectedLead.consent_record_id && (
                  <Button
                    size="sm"
                    variant="link"
                    className="mt-2 p-0"
                    onClick={() => { viewConsentDetails(selectedLead.consent_record_id); setShowLeadDialog(false); }}
                  >
                    View Full Consent Record →
                  </Button>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            {selectedLead?.status === 'pending' && (
              <>
                <Button variant="outline" onClick={() => setShowRejectDialog(true)}>
                  <XCircle className="w-4 h-4 mr-2" />
                  Reject
                </Button>
                <Button onClick={() => handleApproveLead(selectedLead.id)}>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Consent Detail Dialog */}
      <Dialog open={showConsentDialog} onOpenChange={setShowConsentDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-600" />
              TCPA Consent Audit Record
            </DialogTitle>
            <DialogDescription>
              {selectedConsent?.confirmation_id}
            </DialogDescription>
          </DialogHeader>
          {selectedConsent && (
            <div className="space-y-6">
              {/* Consumer Info */}
              <div>
                <h4 className="font-semibold mb-2">Consumer Information</h4>
                <div className="grid grid-cols-2 gap-3 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <label className="text-xs text-gray-500">Name</label>
                    <p>{selectedConsent.consumer_name}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Email</label>
                    <p>{selectedConsent.consumer_email}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Phone</label>
                    <p>{selectedConsent.consumer_phone}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Seller</label>
                    <p>{selectedConsent.seller_name}</p>
                  </div>
                </div>
              </div>

              {/* Consent Flags */}
              <div>
                <h4 className="font-semibold mb-2">Consent Status</h4>
                <div className="grid grid-cols-4 gap-3">
                  <div className={`p-3 rounded-lg text-center ${selectedConsent.consent_contact ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                    <p className="text-xs text-gray-500">Contact</p>
                    <p className={`font-bold ${selectedConsent.consent_contact ? 'text-green-600' : 'text-red-600'}`}>
                      {selectedConsent.consent_contact ? 'AGREED' : 'NOT AGREED'}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg text-center ${selectedConsent.consent_tcpa ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                    <p className="text-xs text-gray-500">TCPA</p>
                    <p className={`font-bold ${selectedConsent.consent_tcpa ? 'text-green-600' : 'text-red-600'}`}>
                      {selectedConsent.consent_tcpa ? 'AGREED' : 'NOT AGREED'}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg text-center ${selectedConsent.consent_sms ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
                    <p className="text-xs text-gray-500">SMS</p>
                    <p className={`font-bold ${selectedConsent.consent_sms ? 'text-green-600' : 'text-gray-400'}`}>
                      {selectedConsent.consent_sms ? 'AGREED' : 'N/A'}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg text-center ${selectedConsent.consent_hipaa ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
                    <p className="text-xs text-gray-500">HIPAA</p>
                    <p className={`font-bold ${selectedConsent.consent_hipaa ? 'text-green-600' : 'text-gray-400'}`}>
                      {selectedConsent.consent_hipaa ? 'AGREED' : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Electronic Signature */}
              {selectedConsent.electronic_signature && (
                <div>
                  <h4 className="font-semibold mb-2">Electronic Signature</h4>
                  <div className="p-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <p className="text-lg font-script italic text-gray-700">{selectedConsent.electronic_signature}</p>
                    <p className="text-xs text-gray-500 mt-2">
                      Signed: {new Date(selectedConsent.consent_timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
              )}

              {/* Consent Language */}
              {selectedConsent.consent_language && (
                <div>
                  <h4 className="font-semibold mb-2">Consent Language Shown</h4>
                  <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200 text-sm">
                    {selectedConsent.consent_language}
                  </div>
                </div>
              )}

              {/* Technical Data */}
              <div>
                <h4 className="font-semibold mb-2">Technical Capture</h4>
                <div className="grid grid-cols-2 gap-3 p-4 bg-gray-50 rounded-lg text-sm">
                  <div>
                    <label className="text-xs text-gray-500">IP Address</label>
                    <p className="font-mono">{selectedConsent.ip_address}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">User Agent</label>
                    <p className="truncate text-xs">{selectedConsent.user_agent}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Screen Resolution</label>
                    <p>{selectedConsent.screen_resolution || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Timezone</label>
                    <p>{selectedConsent.timezone || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Source URL</label>
                    <p className="truncate">{selectedConsent.source_url || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Source Domain</label>
                    <p>{selectedConsent.source_domain || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Third Party */}
              {(selectedConsent.universal_leadid || selectedConsent.trustedform_cert_url) && (
                <div>
                  <h4 className="font-semibold mb-2">Third Party Compliance</h4>
                  <div className="p-4 bg-blue-50 rounded-lg text-sm">
                    {selectedConsent.universal_leadid && (
                      <div>
                        <label className="text-xs text-gray-500">Jornaya LeadID</label>
                        <p className="font-mono">{selectedConsent.universal_leadid}</p>
                      </div>
                    )}
                    {selectedConsent.trustedform_cert_url && (
                      <div className="mt-2">
                        <label className="text-xs text-gray-500">TrustedForm Certificate</label>
                        <a href={selectedConsent.trustedform_cert_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">
                          {selectedConsent.trustedform_cert_url}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div className="p-4 bg-gray-100 rounded-lg text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Consent Timestamp:</span>
                  <span className="font-mono">{new Date(selectedConsent.consent_timestamp).toISOString()}</span>
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-gray-500">Retention Period:</span>
                  <span className="font-medium">{selectedConsent.retention_period}</span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Lead</DialogTitle>
            <DialogDescription>
              Provide a reason for rejecting this lead.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Enter rejection reason..."
            rows={3}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleRejectLead}>Reject Lead</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
